/* OsPlanResultsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;

const PagingOsPlanResultsFeed = require("./PagingOsPlanResultsFeed");
const Renderer = require("./OsPlanResultsViewRenderer");
const LinksColumn = require("./column_renderers/links/LinksComponent");
const DateColumn = require("./column_renderers/date/DateComponent");
const CreateComponent = require("../../../../lmrbcomponents/osplanexplorer/actions/create/CreateComponent");
const FileName = require("@gluenetworks/steve_utilities").fileName;

const EXECUTION_ENTITY_WAIT_MS = 15000;
/**
 * @param {OsPlanEditor} gameEditor
 * @param {UserFeedbackComponent} modalUserFeedback
 */
class OsPlanResultsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        modalUserFeedback,
        steveIO,
        PlanEntity,
        UserSession
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = modalUserFeedback;
        this._plan = undefined;
        this._planEntity = PlanEntity;
        this._executedDeviceIds = undefined;
        this._fileFactory = steveIO.fileFactory;
        this._file = steveIO.File;
        this._pagingExecutionResultsFeed = undefined;
        this._executionEntity = undefined;
        this._pagingExecutionEntityFeed = undefined;
        this._executionWaitCleanup = undefined;
        this._waitingForExecution = false;
        this._recoveryRenderRequested = false;
        this._planListenerRegistered = false;
        this._selectedPlanId = _.get(this._gameEditor._editorTabState, "launchOptions.planId");
        this._fileNamer = FileName;
        this._gridKey = "osPlan_results";
        this._activeOrgId = UserSession.activeOrganization.id;

        this._columnConfig = {
            deviceName: {
                label: "Device",
                priority: 1,
            },
            deviceSku: {
                label: "SKU",
                width: 250,
                priority: 2,
            },
            deviceIp: {
                label: "IP Address",
                width: 250,
                priority: 2,
            },
            status: {
                label: "Execution Status",
                priority: 4,
            },
            progress: {
                label: "Phase",
                priority: 5,
            },
            currentPhase: {
                label: "Message",
                priority: 6,
            },
            percentComplete: {
                label: "% Complete",
                priority: 7,
            },
            startedOn: {
                label: "Started On",
                columnRendererComponent: DateColumn,
                priority: 8,
            },
            completedOn: {
                label: "Completed On",
                columnRendererComponent: DateColumn,
                priority: 9,
            },
            Log: {
                label: "Log",
                priority: 10,
                entityColumn: true,
                columnRendererComponent: LinksColumn,
                context: {
                    gameEditor: this._gameEditor,
                },
            },
        };

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _patchSorting(sorting) {
        return sorting;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        // render() is re-entrant via the "Render" event; re-binding here would leak listeners
        if (this._planListenerRegistered) {
            return;
        }
        this._planListenerRegistered = true;
        this._plan.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );
    }

    _unregisterListeners() {
        this._planListenerRegistered = false;
        this._plan.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    _unregisterGridListeners() {
        if (!_.isEmpty(this._pagingExecutionResultsFeed)) {
            // this._pagingExecutionResultsFeed._feed.removeAllListeners("Page");
            // this._pagingExecutionResultsFeed._feed.removeAllListeners("Repage");
            this._pagingExecutionResultsFeed._feed.removeAllListeners("UpdateEventHandled");
            this._pagingExecutionResultsFeed._feed.removeAllListeners("ChangeFeedInitialized");
            this._pagingExecutionResultsFeed._feed.removeAllListeners("ChangeFeedStarted");
            this._pagingExecutionResultsFeed._feed.removeAllListeners("EntityPropertiesUpdated");
            this._removeUpdateHandlers();
        }
    }

    _removeUpdateHandlers() {
        _.forEach(_.get(this._pagingExecutionResultsFeed._feed, "cache", []), (resultEntity) => {
            resultEntity.removeAllListeners("StatusPropertyUpdated");
        });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get logger() {
        return this._logger;
    }

    get executionEntityData() {
        return this.getExecutionEntityData();
    }

    getExecutionEntityData() {
        return this._executionEntity && this._executionEntity.getPristineProperties();
    }

    get gridKey() {
        return this.getGridKey();
    }

    getGridKey() {
        return this._gridKey;
    }

    get gameEditorRootElement() {
        return this._gameEditor.renderer._rootElem;
    }

    shutdown() {
        this._gameEditor.clearViewActions();
        if (_.isFunction(this._executionWaitCleanup)) {
            this._executionWaitCleanup();
        }
        this._hideLoader();
        this._unregisterListeners();

        return Promise.resolve()
            .then(() => this._renderer.shutdown())
            .then(
                () => {
                    this._logger = null;
                    this._gameEditor = null;
                    this._userSession = null;
                    this._modalUserFeedback = null;
                    this._selectedPlanId = null;
                    this._plan = null;
                    this._pagingExecutionResultsFeed = null;
                    this._pagingExecutionEntityFeed = null;
                    this._executionEntity = null;
                    this._gridKey = null;
                    this._columnConfig = null;
                    this._renderer = null;
                    this._executedDeviceIds = null;

                    Promise.resolve();
                },
                (err) => {
                    this._logger.warn({error: err}, "OsPlanResultsViewComponent.shutdown() error.");
                }
            );
    }

    whenModelIsReady() {
        return this._gameEditor
            .whenPlanIsReady()
            .then(() => {
                this._plan = this._gameEditor.getPlan();
                this._registerListeners();
                return this._getExecutionEntity();
            })
            .then((executionEntityFeed) => this._whenExecutionEntityAvailable(executionEntityFeed))
            .then((executionEntity) => {
                this._executionEntity = executionEntity;
                if (this._executionEntity && this._executionEntity.status === "RUNNING") {
                    this._gameEditor._runWorkId = _.get(this._executionEntity, "workId", undefined);
                }
                if (this._executionEntity) {
                    this._recoveryRenderRequested = false;
                    return this._getResultsFeed(this._executionEntity);
                }
            })
            .catch((error) => {
                if (error !== "no Execution" && error.error !== "no Execution") {
                    this._logger.warn({error}, "Plan Execution Results View model error.");
                }
            });
    }

    _getResultsFeed(_executionEntity) {
        this.emit("ShowCancelAction");
        return this._getExecutionResultsFeed(_executionEntity);
    }

    _getExecutionResultsFeed(executionEntity) {
        return Promise.resolve(executionEntity)
            .then((executionEntity) => {
                if (!executionEntity) {
                    return Promise.reject("no Execution");
                }
                const userSorting = {};
                const patchedSort = this._patchSorting(userSorting);
                return this._getPagingFeed(patchedSort);
            })
            .then(() => {
                this._setQueryDeviceIds(this._pagingExecutionResultsFeed._feed);
                // TODO : Needs cleaning
                this._gameEditor._pagingExecutionFeed = _.values(
                    this._pagingExecutionResultsFeed._feed._cache
                );
                if (_.isEmpty(this._gameEditor._runWorkId)) {
                    this._unregisterGridListeners();
                }

                this.emit("EnableDisableExecutionButtons");
                return Promise.resolve();
            })
            .catch((error) => {
                if (error !== "no Execution" && error.error !== "no Execution") {
                    this._logger.warn({error}, "Plan Execution Results View model error.");
                }
            });
    }

    _getPagingFeed(patchedSort) {
        this._pagingExecutionResultsFeed = new PagingOsPlanResultsFeed({
            feedKey: this._gridKey + "_feed",
            userSession: this._userSession,
            entityDef: this._executionEntity,
            columnConfig: this._columnConfig,
            additionalDataFields: ["osmOptionJTypes", "osmOptions"],
            sort: patchedSort,
        });
        return this.whenFeedIsReady();
    }

    _showLoader(msg) {
        if (_.isNil(this._modalUserFeedback)) {
            return;
        }
        this._modalUserFeedback.showLoadingProgress(msg);
    }
    _hideLoader() {
        if (_.isNil(this._modalUserFeedback)) {
            return Promise.resolve();
        }
        return this._modalUserFeedback.hideDisplayed();
    }

    whenFeedIsReady() {
        return this._pagingExecutionResultsFeed.whenFeedReady().catch((err) => {
            this._logger.warn({error: err}, "OsPlanResultsViewComponent.whenFeedReady() error.");
        });
    }
    _setQueryDeviceIds(feed) {
        if (!_.isEmpty(feed._cache)) {
            this._executedDeviceIds = Object.keys(feed._cache);
        }
    }
    onUserSorted(sortData) {
        sortData = [].concat(sortData);
        let sort = {};
        sortData.forEach((sortData) => {
            if (!sortData || !sortData.sortColumn || !this._columnConfig[sortData.sortColumn]) {
                return;
            }
            const sortCriteria =
                this._columnConfig[sortData.sortColumn].valuePath ||
                this._columnConfig[sortData.sortColumn].path ||
                sortData.sortColumn;

            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });
        this._pagingExecutionResultsFeed.setSort(this._patchSorting(sort));
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onPristinePropertiesChanged(patch) {
        if (_.isEmpty(patch) || this._gameEditor._planDeleted) {
            return;
        }
        // Execution landed after the empty state was rendered - rebuild the view once
        if (
            _.isNil(this._executionEntity) &&
            !this._waitingForExecution &&
            !this._recoveryRenderRequested &&
            _.includes(["RUNNING", "ERROR", "COMPLETE", "CANCELED"], patch.status)
        ) {
            this._recoveryRenderRequested = true;
            this.emit("Render");
            return;
        }
        if (_.includes(["ERROR", "COMPLETE", "CANCELED"], patch.status)) {
            if (!_.isEmpty(this._gameEditor._runWorkId)) {
                this.updateFeed().then(() => {
                    this.emit("UpdateGrid");
                });
                this._gameEditor._runWorkId = undefined;
            } else {
                this.emit("Render");
            }
            this._unregisterGridListeners();
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/
    /**
     * Gets Execution Entity, if it is not provided
     * @returns {Promise<ConfigAuditExecutionEntity | Error>}
     * @private
     */

    _getExecutionEntity() {
        // startRelationshipFeed hands back the same underlying feed, so never wrap it twice
        if (_.isNil(this._pagingExecutionEntityFeed)) {
            this._pagingExecutionEntityFeed = new PagingOsPlanResultsFeed({
                feedKey: this._gridKey + "_execution_entity_feed",
                entityDef: this._plan,
            });
        }
        return this._pagingExecutionEntityFeed.whenFeedReady();
    }

    /**
     * Resolves with the Execution entity, waiting on the live relationship feed when the record
     * has not landed yet. Bounded by EXECUTION_ENTITY_WAIT_MS so the view can never hang.
     * @param {PagingOsPlanResultsFeed} pagingFeed
     * @returns {Promise<Entity|undefined>}
     * @private
     */
    _whenExecutionEntityAvailable(pagingFeed) {
        const feed = _.get(pagingFeed, "feed");
        const cached = _.values(_.get(pagingFeed, "cache", {}))[0];

        if (!_.isNil(cached) || _.isNil(feed)) {
            return Promise.resolve(cached);
        }
        // Only a run started in this editor session can still have an Execution record in flight
        if (_.isEmpty(this._gameEditor._runWorkId)) {
            return Promise.resolve(undefined);
        }

        this._waitingForExecution = true;
        this._showLoader("Preparing execution results...");
        this.emit("Loading", "Preparing execution results...");

        return new Promise((resolve) => {
            const listeners = [];
            const timer = {};
            const finish = (entity) => {
                if (!this._waitingForExecution) {
                    return;
                }
                this._waitingForExecution = false;
                this._executionWaitCleanup = undefined;
                clearTimeout(timer.id);
                _.forEach(listeners, (pair) => feed.removeListener(pair[0], pair[1]));
                resolve(entity);
            };
            const firstCached = () => _.values(_.get(feed, "cache", {}))[0];
            listeners.push(["EntityAdded", (entity) => finish(entity)]);
            listeners.push([
                "Page",
                () => {
                    if (!_.isNil(firstCached())) {
                        finish(firstCached());
                    }
                },
            ]);

            // Arm the timeout before attaching listeners so finish() can never run un-armed
            timer.id = setTimeout(() => {
                this._logger.warn(
                    {planId: this._selectedPlanId, runWorkId: this._gameEditor._runWorkId},
                    "OsPlanResultsViewComponent - timed out waiting for the Execution entity."
                );
                finish(firstCached());
            }, EXECUTION_ENTITY_WAIT_MS);
            _.forEach(listeners, (pair) => feed.addListener(pair[0], pair[1]));
            this._executionWaitCleanup = () => finish(undefined);
        });
    }

    updateFeed() {
        if (this._pagingExecutionResultsFeed && this._pagingExecutionResultsFeed.shutdown) {
            return this._pagingExecutionResultsFeed
                .shutdown()
                .then(() => {
                    return this._executionEntity.stopRelationshipFeed("results");
                })
                .then(() => {
                    this._pagingExecutionResultsFeed = undefined;

                    const userSorting = {};
                    const patchedSort = this._patchSorting(userSorting);
                    return this._getPagingFeed(patchedSort);
                })
                .then(() => {
                    if (!_.isEmpty(this._renderer._planResultsGrid)) {
                        return this._renderer._planResultsGrid.shutdown();
                    }
                    return Promise.resolve();
                })
                .then(() => {
                    this._renderer._planResultsGrid = undefined;
                });
        }
        return Promise.resolve();
    }

    _cancelRemaining() {
        return Promise.resolve()
            .then(() => {
                return this._plan.cancelPlanWork(this._gameEditor._runWorkId);
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "OsPlanResultsViewComponent._cancelRemaining() error."
                );
                return Promise.reject();
            });
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    async _createPlanFromError() {
        //looking for -v<number> at end of name and increment
        const regexVersion = /^(.*)-v([0-9]+)$/;
        let newName = _.get(this._plan, "name", "unknown");
        const results = newName.match(regexVersion);

        if (results && results[2]) {
            newName = results[1] + "-v" + (parseInt(results[2]) + 1);
        } else {
            newName = newName + "-v1";
        }

        // now check for duplicated name, if so, keep 'incrementing' like we do on clone
        let unique;
        do {
            const exist = await this._planEntity.retrieveOsUpgradePlanByName(
                this._activeOrgId,
                newName,
                {id: true}
            );
            unique = true;
            if (exist) {
                newName = newName + "-1";
                unique = false;
            }
        } while (!unique);

        return this._modalUserFeedback
            .displayCustom(
                new CreateComponent(
                    this._logger,
                    "Please specify a name for this cloned plan.",
                    "",
                    newName
                ),
                {dontHide: true}
            )
            .then((result) => {
                return this._modalUserFeedback.displayLoading().then(() => {
                    return this._plan.cloneErrors(result);
                });
            })
            .then((result) => {
                this._gameEditor.launchNewTab(result);
            })
            .then(() => {
                return this._modalUserFeedback.hideDisplayed();
            })
            .catch((err) => {
                if (err.userRejected) {
                    this._modalUserFeedback.hideDisplayed();
                } else {
                    this._logger.warn(
                        {error: err},
                        "OsPlanResultsViewComponent._createPlanFromError() - Error"
                    );
                    return this._modalUserFeedback.displayError(err);
                }
            });
    }

    _downloadWorkResults() {
        return this._plan
            .runPlanReportWork()
            .then((result) => {
                let fileName = this._fileNamer({
                    solution: "OSM",
                    general: "PlanExecReport",
                    specific: this._plan.pristineProperties.name,
                    suffix: "csv",
                });
                let file = new this._file(fileName, null, result);
                this._fileFactory.saveFile(file);
                return Promise.resolve();
            })
            .catch((error) => this._logger.warn({error}, "Plan onDownloadWorkResults - error"));
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = OsPlanResultsViewComponent;

OsPlanResultsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.io",
    "$teve.entities.OsUpgradePlan",
    "$teve.userSession",
];

OsPlanResultsViewComponent.$displayDetails = {
    type: "View",
    name: "OS Plan Execution",
    icon: "common.results_link",
    shown: true,
    title: "OS Plan Execution",
    order: 5,
};
